import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

const STATUS_STYLES = {
  pass: {
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/10",
    text: "text-emerald-300",
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
    label: "OK",
  },
  warn: {
    border: "border-amber-500/30",
    bg: "bg-amber-500/10",
    text: "text-amber-300",
    icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
    label: "Let op",
  },
  fail: {
    border: "border-rose-500/30",
    bg: "bg-rose-500/10",
    text: "text-rose-300",
    icon: <XCircle className="w-5 h-5 text-rose-400" />,
    label: "Fout",
  },
};

const OVERALL_STYLES = {
  pass: { text: "Alles ziet er goed uit", cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
  warn: { text: "Er zijn waarschuwingen", cls: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  fail: { text: "Kritieke fouten gevonden — actie vereist", cls: "bg-rose-500/10 border-rose-500/30 text-rose-300" },
};

export const AdminHealth = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/health`, { headers: adminHeaders() });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/sportly-admin", { replace: true });
      } else {
        toast.error("Health check mislukt");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/sportly-admin", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin", { replace: true });
  };

  const overall = data?.overall || "pass";
  const overallStyle = OVERALL_STYLES[overall];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-health-page">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/sportly-admin/dashboard")}
              className="text-slate-400 hover:text-white flex items-center gap-1.5"
              data-testid="health-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Admin</span>
            </button>
            <span className="text-slate-700">/</span>
            <span className="font-bold text-sm">Health check</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 h-9"
              data-testid="health-refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Bezig..." : "Vernieuwen"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="health-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-4">
        {data && (
          <div
            className={`rounded-lg border p-4 ${overallStyle.cls}`}
            data-testid="health-overall-status"
          >
            <div className="text-sm font-semibold">{overallStyle.text}</div>
            <div className="mt-1 text-xs opacity-80">
              Laatste check: {new Date(data.checked_at).toLocaleString("nl-NL")}
            </div>
          </div>
        )}

        {loading && !data && (
          <div className="text-center py-10 text-slate-500 text-sm">Bezig met controleren…</div>
        )}

        <div className="space-y-2">
          {(data?.checks || []).map((c, i) => {
            const style = STATUS_STYLES[c.status] || STATUS_STYLES.warn;
            return (
              <div
                key={i}
                className={`rounded-md border p-3 flex items-start gap-3 ${style.border} ${style.bg}`}
                data-testid={`health-check-${i}`}
              >
                <div className="mt-0.5 shrink-0">{style.icon}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-white text-sm">{c.name}</span>
                    <span className={`text-xs font-medium ${style.text}`}>{style.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-300">{c.message}</p>
                  {c.detail && (
                    <p className="mt-1 text-xs text-slate-500 font-mono">{c.detail}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default AdminHealth;
